import { useDefaultDataLayer } from '../useDataLayer';
import { useSwiperInteractionDetector } from '../../composables/useSwiperInteractionDetector';

/**
 * @typedef {Object} CarouselTrackingOptions
 * @property {string} [componentName='Carousel'] - The name of the component containing the carousel. Defaults to 'Carousel'.
 * @property {string} [location='body'] - The location of the carousel on the page (e.g., 'header', 'body', 'footer'). Defaults to 'body'.
 * @property {string} [labelPrev='click_backwards'] - The link text label for tracking 'previous' carousel interactions.
 * @property {string} [labelNext='click_forwards'] - The link text label for tracking 'next' carousel interactions.
 */

/**
 * @typedef {Object} CarouselTrackingMethods
 * @property {function(Object=): void} trackNextSlide - Function to track a 'next' slide interaction.
 * @property {function(Object=): void} trackPrevSlide - Function to track a 'previous' slide interaction.
 */

/**
 * Hook for tracking carousel interactions, specifically scroll events (next/previous).
 * This hook provides methods to register carousel scroll events with a data layer,
 * allowing for flexible tracking of 'next' and 'previous' slide movements.
 *
 * @param {CarouselTrackingOptions} [options={}] - Configuration options for carousel tracking.
 * @param {import('vue').Ref<Object | null>} swiperInstanceRef - Reference to the Swiper instance.
 * @returns {CarouselTrackingMethods} An object containing tracking methods for carousel interactions.
 */
export function useCarouselTracking(options = {}, swiperInstanceRef = null) {
  const {
    componentName = 'Carousel',
    location = 'body',
    labelPrev = 'click_backwards',
    labelNext = 'click_forwards',
  } = options;
  const { registerInteraction } = useDefaultDataLayer();

  /**
   * A curried function that generates a specific carousel scroll tracking function.
   *
   * The first call defines the `linkText` for the event
   * and allows for `initialAdditionalData` that will be merged with any data
   * provided in the second call.
   * The returned function then takes more specific `additionalData` to register the event.
   *
   * @private
   * @param {string} linkText - The specific link text to associate with this carousel scroll event.
   * @param {Object} [initialAdditionalData={}] - Optional initial additional data to include with the event.
   *   This data will be merged with (and potentially overridden by) data provided in the second call.
   * @returns {function(Object=): void} A function that takes specific additional data to register the
   *   carousel scroll event.
   *   - `additionalData` {Object}: [Optional] Additional data specific to this particular event,
   *     which will be merged with `initialAdditionalData`.
   */
  const trackCarouselScroll =
    (linkText, initialAdditionalData = {}) =>
    (additionalData = {}) => {
      registerInteraction({
        event: 'carousel_scroll',
        link_text: linkText,
        component: componentName,
        intent: 'carousel_interaction',
        event_location: location,
        ...initialAdditionalData,
        ...additionalData,
      });
    };

  /**
   * Tracks a 'next' slide interaction in the carousel.
   *
   * @param {Object} [additionalData={}] - Optional additional data to include with the 'carousel_scroll' event.
   * @returns {void}
   */
  const trackNextSlide = trackCarouselScroll(labelNext);

  /**
   * Tracks a 'previous' slide interaction in the carousel.
   *
   * @param {Object} [additionalData={}] - Optional additional data to include with the 'carousel_scroll' event.
   * @returns {void}
   */
  const trackPrevSlide = trackCarouselScroll(labelPrev);

  const handleScrollTracked = (direction) => {
    if (direction === 'next') {
      trackNextSlide();
    } else if (direction === 'prev') {
      trackPrevSlide();
    }
  };

  useSwiperInteractionDetector(swiperInstanceRef, handleScrollTracked);

  return {
    trackNextSlide,
    trackPrevSlide,
  };
}
